import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { EnvelopeIcon, BellIcon } from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import { useAccessibility } from '../../context/AccessibilityContext'; 
import { SpeakerWaveIcon, SpeakerXMarkIcon } from '@heroicons/react/24/solid'; // 2. Import Speaker icons

export default function TopNavbar() {
  const { darkMode, toggleTheme } = useTheme();
  const { user, token } = useAuth();
  const { soundEnabled, toggleSound } = useAccessibility(); // 3. Get sound state and function
  
  const [notifications, setNotifications] = useState([]);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const brandColor = '#e46809';

  useEffect(() => {
    const fetchNotifications = async () => {
      if (token) {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/notifications`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setNotifications(data);
          }
        } catch (error) {
          console.error("Failed to fetch notifications:", error);
        }
      } else {
        setNotifications([]);
      }
    };
    fetchNotifications();
  }, [token]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownRef]);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const markAsRead = async () => {
    if (unreadCount > 0 && token) {
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      await fetch(`${process.env.REACT_APP_API_URL}/notifications/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    }
  };

  const handleDropdownToggle = () => {
    const newOpenState = !isDropdownOpen;
    setDropdownOpen(newOpenState);
    if (newOpenState && unreadCount > 0) {
      markAsRead();
    }
  };

  return (
    <div className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 py-2 px-4 text-sm w-full">
      <div className="container mx-auto flex justify-between items-center">
        <a href="mailto:info@givetechtotheblind.com" className="flex items-center hover:text-orange-500 dark:hover:text-orange-400 transition-colors duration-200">
          <EnvelopeIcon className="w-4 h-4 mr-2 text-orange-500" />
          <span>info@givetechtotheblind.com</span>
        </a>
        
        <div className="flex items-center gap-4">
          {user && (
            <div className="relative" ref={dropdownRef}>
              <button onClick={handleDropdownToggle} className="relative text-gray-600 dark:text-gray-300 hover:text-orange-500">
                <BellIcon className="w-6 h-6" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center font-bold">
                    {unreadCount}
                  </span>
                )}
              </button>
             {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-xl border dark:border-gray-700 z-[9999]">
                  <div className="p-3 font-bold border-b dark:border-gray-600 text-gray-800 dark:text-white">Notifications</div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length > 0 ? (
                      notifications.map(n => (
                        <Link 
                          to={n.link_to || '#'} 
                          key={n.id} 
                          onClick={() => setDropdownOpen(false)}
                          className={`block p-3 border-b dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700 ${!n.is_read ? 'bg-orange-50 dark:bg-orange-900/30' : ''}`}
                        >
                          <p className="text-sm text-gray-800 dark:text-gray-200">{n.message}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                          </p>
                        </Link>
                      ))
                    ) : (
                      <p className="p-4 text-center text-gray-500">You have no notifications.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* === ADDED SOUND TOGGLE BUTTON === */}
          <button 
            onClick={toggleSound}
            title={soundEnabled ? 'Disable sound effects' : 'Enable sound effects'}
            className="p-2 rounded-full text-gray-600 dark:text-gray-300 hover:text-orange-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 dark:focus:ring-offset-gray-800"
          >
            {soundEnabled ? (
              <SpeakerWaveIcon className="w-5 h-5" />
            ) : (
              <SpeakerXMarkIcon className="w-5 h-5" />
            )}
          </button>
          {/* ================================ */}
          
          <button 
            onClick={toggleTheme}
            title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            className="p-2 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 dark:focus:ring-offset-gray-800 border border-gray-300 dark:border-gray-600"
          >
            {darkMode ? '☀️' : '🌙'}
          </button>
        </div>
      </div>
    </div>
  );
}